import { useEffect, useState } from "react";
import { Grid, Typography, Button, Breadcrumbs } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { makeStyles } from "@material-ui/core/styles";
import AccordionList from "./AccordionList";
import PieChart from "./PieChart";
import StackBarChart from "./StackBarChart";
import { getAllAssignedBooksByTrainer } from "../Services";
import { getClassNames } from "../../../Js/Common";
import { commonSliceActions } from "../../../Store/Slices/CommonSlice";

import {
  map,
  chain,
  groupBy,
  uniqBy,
  intersection,
  find,
  uniq,
  findIndex,
  sortBy,
} from "lodash";
import BarChart from "./BarChart";

const trainerWiseSeriesMetadata = [
  {
    name: "Assigned Books",
    field: "totalBooks",
  },
  {
    name: "Attempted Books",
    field: "attemptedBooks",
  },
];

const assignmentWiseSeriesMetadata = [
  {
    name: "No. of Learners",
    field: "numberOfStudentAssigned",
  },
  {
    name: "No. of Learners Attempted",
    field: "numberOfStudentAttempted",
  },
];

const LibraryReport = (props) => {
  let { startDate, endDate, selectedTrainers } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  let [trainerwiseData, setTrainerWiseData] = useState([]);
  let [selectedTrainerId, setSelectedTrainerId] = useState(null);
  let [selectedTrainerName, setSelectedTrainerName] = useState(null);
  let [classwiseData, setClasswiseData] = useState([]);
  let [chartLabelField, setChartLabelField] = useState("trainerName");
  let [selectedClassId, setSelectedClassId] = useState(null);
  let [selectedClassName, setSelectedClassName] = useState(null);
  let [seriesMetaData, setSeriesMetaData] = useState(trainerWiseSeriesMetadata);
  let [assignmentwiseData, setAssignmentwiseData] = useState([]);

  const userInfo = useSelector((state) => state.loggedInUserInfo.userInfo);

  useEffect(() => {
    if (selectedTrainers.length > 0) {
      dispatch(commonSliceActions.setLoader(true));
      fetchBooksAssignedByTrainers(map(selectedTrainers, "id"));
    } else {
      setTrainerWiseData([]);
    }

    return () => {
      props.inDetailOn(false);
    }
  }, [startDate, endDate, selectedTrainers, userInfo]);

  let fetchBooksAssignedByTrainers = async (createdBy) => {
    let { AllBooksAssigned, uniqAssignmentsAttempted } = await getAllBasicData(
      createdBy
    );
    let trainerWiseBooks = chain(AllBooksAssigned.data)
      // Group the elements of Array based on `color` property
      .groupBy("created_by")
      // `key` is group's name (color), `value` is the array of objects
      .map((value, key) => ({ created_by: key, assignments: value }))
      .value();

    let attemptedBook = map(uniqAssignmentsAttempted, "target");
    trainerWiseBooks = trainerWiseBooks.map((val) => {
      let trainerBooks = map(val.assignments, "uid");
      let intersectionArr = intersection(attemptedBook, trainerBooks);
      return {
        ...val,
        totalBooks: val.assignments.length,
        attemptedBooks: intersectionArr.length,
        trainerName: find(selectedTrainers, { id: val.created_by }).value,
      };
    });
    setTrainerWiseData(trainerWiseBooks);
    dispatch(commonSliceActions.setLoader(false));
  };

  let getAllBasicData = async (createdBy, attemptedAll = false) => {
    let AllBooks = await getAllAssignedBooksByTrainer("tasks", {
      tasktype: "library",
      site_uuid: userInfo.site_uuid,
      created_by: createdBy,
      $limit: -1,
    });

    let taskids = map(AllBooks.data, "uid");

    let AllBooksAssigned = await getAllAssignedBooksByTrainer(
      "teacher-reports",
      {
        reportType: "libraryReport",
        site_uuid: userInfo.site_uuid,
        user_uid: createdBy,
        fromDate: startDate,
        toDate: endDate,
        tasksArr: taskids,
      }
    );

    let totalAssignments = map(AllBooksAssigned.data, "uid");
    let getAllAttemptedBooks = await getAllAssignedBooksByTrainer("user-logs", {
      target: totalAssignments,
      site_uuid: userInfo.site_uuid,
      verb: "attemptAssignment",
    });

    let uniqAssignmentsAttempted = attemptedAll
      ? getAllAttemptedBooks.data.data
      : uniqBy(getAllAttemptedBooks.data.data, "target");

    return {
      AllBooksAssigned,
      uniqAssignmentsAttempted,
    };
  };

  let fetchDataWithClass = async () => {
    let { AllBooksAssigned, uniqAssignmentsAttempted } = await getAllBasicData(
      [selectedTrainerId],
      true
    );
    let allLeranersId = [];
    AllBooksAssigned.data.forEach((val) => {
      allLeranersId = allLeranersId.concat(val.learners.learners);
    });

    let classIds = await getAllAssignedBooksByTrainer("users", {
      $select: ["uid", "belongs_to"],
      uid: allLeranersId,
      site_uuid: userInfo.site_uuid,
      $limit: -1,
    });

    let classNames = await getClassNames(userInfo.site_uuid);

    return {
      AllBooksAssigned,
      uniqAssignmentsAttempted,
      classIds,
      classNames,
    };
  };

  useEffect(() => {
    let innerFun = async () => {
      let classWiseData = [];
      let { AllBooksAssigned, uniqAssignmentsAttempted, classIds, classNames } =
        await fetchDataWithClass();
      AllBooksAssigned.data.map((val) => {
        val.learners.learners.forEach((id) => {
          let classId = find(classIds.data, { uid: id });
          let pos = findIndex(classWiseData, { classid: classId.belongs_to });
          let assignmentPos = -1;
          if (pos > -1) {
            assignmentPos = findIndex(classWiseData[pos]["assignments"], {
              uid: val.uid,
            });
          }
          let classname = find(classNames, { id: classId.belongs_to });
          if (classId && pos == -1) {
            classWiseData.push({
              classid: classId.belongs_to,
              classname: classname.value,
              assignments: [val],
              totalBooks: 1,
              attemptedAssignments:
                findIndex(uniqAssignmentsAttempted, { actor: id }) > -1
                  ? [val]
                  : [],
              attemptedBooks:
                findIndex(uniqAssignmentsAttempted, { actor: id }) > -1 ? 1 : 0,
            });
          } else {
            if (assignmentPos == -1) {
              classWiseData[pos]["assignments"] = [
                ...classWiseData[pos]["assignments"],
                val,
              ];
              classWiseData[pos]["totalBooks"] =
                classWiseData[pos]["totalBooks"] + 1;
            }
            if (
              findIndex(classWiseData[pos]["attemptedAssignments"], {
                uid: val.uid,
              }) == -1 &&
              findIndex(uniqAssignmentsAttempted, {
                actor: id,
                target: val.uid,
              }) > -1
            ) {
              classWiseData[pos]["attemptedAssignments"] = [
                ...classWiseData[pos]["attemptedAssignments"],
                val,
              ];
              classWiseData[pos]["attemptedBooks"] =
                classWiseData[pos]["attemptedBooks"] + 1;
            }
          }
        });
      });

      classWiseData = sortBy(classWiseData, ["classname"]);
      setChartLabelField("classname");
      setClasswiseData(classWiseData);
    };
    if (selectedTrainerId) {
      innerFun();
    }
  }, [selectedTrainerId, startDate, endDate]);

  useEffect(() => {
    let innerFun = async () => {
      let { AllBooksAssigned, uniqAssignmentsAttempted, classIds, classNames } =
        await fetchDataWithClass();

      let assignmentDataByBook = [];
      AllBooksAssigned.data.forEach((val) => {
        let numberOfStudentAssigned = 0;
        let numberOfStudentAttempted = 0;
        val.learners.learners.forEach((id) => {
          let classId = find(classIds.data, { uid: id });
          if (classId.belongs_to == selectedClassId) {
            numberOfStudentAssigned += 1;
            if (
              findIndex(uniqAssignmentsAttempted, {
                actor: id,
                target: val.uid,
              }) > -1
            ) {
              numberOfStudentAttempted += 1;
            }
          }
        });

        if (numberOfStudentAssigned > 0) {
          assignmentDataByBook.push({
            ...val,
            numberOfStudentAssigned,
            numberOfStudentAttempted,
          });
        }
      });

      setSeriesMetaData(assignmentWiseSeriesMetadata);
      setChartLabelField("title");
      setAssignmentwiseData(assignmentDataByBook);
    };
    if (selectedClassId) {
      innerFun();
    }
  }, [selectedClassId, startDate, endDate]);

  const backClick = () => {
    if (selectedClassId) {
      setAssignmentwiseData([]);
      setSelectedClassId(null);
      setSelectedClassName(null);
      setSelectedTrainerId((val) => {
        return val;
      });
      setChartLabelField("classname");
      setSeriesMetaData(trainerWiseSeriesMetadata);
    }
    if (!selectedClassId && selectedTrainerId) {
      setClasswiseData([]);
      setSelectedTrainerId(null);
      setSelectedTrainerName(null);
      props.inDetailOn(false);
      if (selectedTrainers.length > 0) {
        fetchBooksAssignedByTrainers(map(selectedTrainers, "id"));
      }
      setChartLabelField("trainerName");
      setSeriesMetaData(trainerWiseSeriesMetadata);
    }
  };

  return (
    <Grid container   sx={{ height: '68vh', overflow: 'auto', px: 1 }}>
      {selectedTrainerId && (
        <>
          <Grid item xs={10} sx={{ px: 3, textAlign: "left" }}>
            <Breadcrumbs
              aria-label="breadcrumb"
              separator={<NavigateNextIcon color="primary" fontSize="small" />}
            >
              {selectedTrainerId && (
                <Typography key="1" color="primary">
                  {selectedTrainerName}
                </Typography>
              )}
              {selectedClassId && (
                <Typography key="2" color="primary">
                  {selectedClassName}
                </Typography>
              )}
            </Breadcrumbs>
          </Grid>
          <Grid item xs={2} sx={{ px: 1, textAlign: "right" }}>
            <Button
              variant="standard"
              fullWidth
              sx={{
                color: "#DB8829",
                fontSize: "1rem",
                fontWeight: "700",
                border: 1,
                borderColor: "#DB8829",
                borderRadius: 10,
              
                "@media (min-width: 1636px)": { fontSize: "1.4rem" },
              }}
              onClick={backClick}
            >
              Back
            </Button>
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        {(assignmentwiseData.length > 0 ||
          classwiseData.length > 0 ||
          trainerwiseData.length > 0) && (
            <BarChart
              barData={
                assignmentwiseData.length > 0
                  ? assignmentwiseData
                  : classwiseData.length > 0
                    ? classwiseData
                    : trainerwiseData
              }
              isHorizontal={false}
              chartWidth={"95%"}
              chartHeight={"500px"}
              seriesMetaData={seriesMetaData}
              labelField={chartLabelField}
              click={(index) => {
                if (selectedTrainerId && selectedClassId) {
                  return false;
                } else {
                  if (selectedTrainerId) {
                    setSelectedClassId(classwiseData[index]["classid"]);
                    setSelectedClassName(classwiseData[index]["classname"]);
                    props.inDetailOn(true);
                  } else {
                    setSelectedTrainerId(trainerwiseData[index]["created_by"]);
                    setSelectedTrainerName(trainerwiseData[index]["trainerName"]);
                    props.inDetailOn(true);
                  }
                }
              }}
            />
          )}
      </Grid>
    </Grid>
  );
};
const useStyles = makeStyles({
  scrollBar: {
    "&::-webkit-scrollbar": {
      width: "0.6em",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#ffe600ff",
      "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)",
      borderRadius: 5,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#5f5600ff",
      borderRadius: 5,
    },
  },
});
export default LibraryReport;
